import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Switch } from '../ui/switch'
import { Label } from '../ui/label'
import { companyAPI } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { toast } from '../../hooks/use-toast'
import {
  BookOpen,
  CheckCircle2,
  AlertCircle,
  FileText,
  Clock,
  Trophy,
  Lock,
  Loader2
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function DemoGuidelinesModal({ open, onClose }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [guidelinesRead, setGuidelinesRead] = useState(false)
  const [readingTimestamp, setReadingTimestamp] = useState(null)

  const isIntakeCompleted = user?.companyId && user?.intakeCompleted

  // Fetch existing guidelines status when modal opens
  useEffect(() => {
    if (open) {
      fetchGuidelinesStatus()
    }
  }, [open])

  const fetchGuidelinesStatus = async () => {
    try {
      const response = await companyAPI.getGuidelinesStatus()
      if (response.data.success) {
        setGuidelinesRead(response.data.guidelinesRead || false)
        setReadingTimestamp(response.data.readAt)
      }
    } catch (error) {
      console.log('No guidelines status found')
    }
  }

  const handleToggleRead = async (checked) => {
    if (!isIntakeCompleted) {
      toast.warning('Please complete the Company Details & Logo first to unlock this feature.')
      return
    }

    setLoading(true)
    try {
      const response = await companyAPI.markGuidelinesAsRead(checked) // Pass the checked state to the API
      if (response.data.success) {
        setGuidelinesRead(checked)
        if (checked) {
          setReadingTimestamp(new Date())
        } else {
          setReadingTimestamp(null)
        }
        toast.success(checked ? 'Guidelines marked as read' : 'Guidelines marked as unread')
      }
    } catch (error) {
      console.error('Error updating guidelines status:', error)
      toast.error('Failed to save progress. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenGuidelines = () => {
    // Navigate to Demo Guidelines page
    navigate('/company/guidelines')
    onClose() // Close the modal after navigation
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg sm:text-xl">Demo Guidelines Review</DialogTitle>
              <DialogDescription className="mt-1 text-xs sm:text-sm">
                Essential information for your successful demo presentation at IFTA 2025
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-4 px-4 sm:px-6 overflow-y-auto flex-1">
          {/* Status Badge */}
          {guidelinesRead && (
            <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-gray-600 shrink-0" />
                <span className="text-xs sm:text-sm text-gray-700 font-medium">Guidelines Reviewed</span>
              </div>
              {readingTimestamp && (
                <span className="text-xs text-gray-600 shrink-0">
                  {new Date(readingTimestamp).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          {/* Key Information Cards */}
          <div className="grid gap-2 sm:grid-cols-2">
            <Card className="p-3 border-gray-200 bg-gray-50/50">
              <div className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm">Demo Duration</h4>
                  <p className="text-xs text-gray-600">7 minutes maximum per presentation</p>
                </div>
              </div>
            </Card>

            <Card className="p-3 border-gray-200 bg-gray-50/50">
              <div className="flex items-start gap-2">
                <FileText className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm">Pitch Framework</h4>
                  <p className="text-xs text-gray-600">10 mandatory sections to cover</p>
                </div>
              </div>
            </Card>

            <Card className="p-3 border-gray-200 bg-gray-50/50">
              <div className="flex items-start gap-2">
                <Trophy className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm">Award Categories</h4>
                  <p className="text-xs text-gray-600">Startup & Scaleup of the Year</p>
                </div>
              </div>
            </Card>

            <Card className="p-3 border-gray-200 bg-gray-50/50">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm">Evaluation Criteria</h4>
                  <p className="text-xs text-gray-600">7 parameters with weighted scoring</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Important Notice */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>Why Review Guidelines?</span>
            </h4>
            <ul className="space-y-1 sm:space-y-1.5 text-xs sm:text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5 shrink-0">•</span>
                <span>Understand the exact pitch framework expected by judges</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5 shrink-0">•</span>
                <span>Learn about technical requirements and venue setup</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5 shrink-0">•</span>
                <span>Review evaluation criteria to optimize your presentation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5 shrink-0">•</span>
                <span>Access sample demo videos and best practices</span>
              </li>
            </ul>
          </div>

          {/* Intake Not Completed Warning */}
          {!isIntakeCompleted && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <Lock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-amber-900 mb-1 text-sm sm:text-base">Complete Company Details First</h4>
                  <p className="text-xs sm:text-sm text-amber-700">
                    You need to complete the Company Details & Logo form before you can mark the guidelines as read.
                    You can still view the guidelines for reference.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-4 sm:px-6 py-4 border-t shrink-0 flex-col-reverse sm:flex-row gap-3 sm:gap-2">
          <Button
            onClick={handleOpenGuidelines}
            className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
          >
            Read Guidelines
          </Button>

          <div className="flex items-center justify-between sm:justify-end sm:ml-auto gap-2">
            <Label
              htmlFor="guidelines-read"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {loading ? 'Saving...' : 'Mark as Done'}
            </Label>
            <Switch
              id="guidelines-read"
              checked={guidelinesRead}
              onCheckedChange={handleToggleRead}
              disabled={!isIntakeCompleted || loading}
            />
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}